


import React, { useState, useEffect } from "react";
import bannerimg from "../../assets/contact/bacground.png";
import {
  Shield,
  UserCog,
  Lock,
  HardDrive,
  Globe,
  Users,
  Calendar,
  CheckCircle,
  FileText,
  Link,
  Cookie,
  XCircle,
} from "lucide-react";

// --- Sidebar Navigation Item ---
const NavItem = ({ icon: Icon, title, isActive }) => {
  const activeClasses =
    "bg-gradient-to-r from-[#00AFEF] to-[#2F56A6] text-white shadow-lg";
  const inactiveClasses =
    "bg-white text-gray-800 shadow-md border-r-4 border-transparent hover:border-r-[#00AFEF]";

  return (
    <a
      href={`#${title.toLowerCase().replace(/\s/g, "-")}`}
      className={`flex items-center p-3 my-2 rounded-lg transition-all duration-300 ${
        isActive ? activeClasses : inactiveClasses
      }`}
    >
      <Icon
        className={`w-5 h-5 mr-3 flex-shrink-0 ${
          isActive ? "text-white" : "text-[#00AFEF]"
        }`}
      />
      <span className="font-medium text-base">{title}</span>
    </a>
  );
};

// --- Policy Detail Item ---
const PolicyDetailItem = ({ title, content }) => (
  <li className="flex items-start mb-4">
    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-1.5 mr-3 text-[#00AFEF]" />
    <p className="text-gray-600 text-[16px] leading-relaxed">
      <strong className="font-semibold text-[#2F56A6]">{title}:</strong>{" "}
      {content}
    </p>
  </li>
);

// --- Main Component ---
const PrivacyPolicy = () => {
 const [activeSection, setActiveSection] = useState("Introduction");

const navItems = [
  { title: "Introduction", icon: FileText },
  { title: "Information We Collect", icon: Shield },
  { title: "How We Use Your Information", icon: UserCog },
  { title: "Information Sharing and Disclosure", icon: Globe },
  { title: "Data Security", icon: Lock },
  { title: "Data Retention", icon: HardDrive },
  { title: "Your Rights", icon: Users },
  { title: "Cookies and Tracking", icon: Cookie },
  { title: "Third-Party Links", icon: Link },
  { title: "Updates to This Policy", icon: Calendar },
  { title: "Contact Us", icon: XCircle },
];


  return (
    <>
      {/* --- Banner --- */}
      <div className="relative w-full h-[300px] md:h-auto min-h-52">
        <img
          src={bannerimg}
          alt="Privacy Policy Banner"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 py-12 md:pb-1 xs:pb-1">
          <h1 className="text-white text-4xl md:text-6xl lg:text-[80px] font-normal font-goldman text-center leading-tight">
            Privacy Policy
          </h1>
          <p className="text-[#D9D9D9] text-base md:text-xl lg:text-[22px] text-center max-w-2xl font-dmsans mt-2">
            Your privacy matters to us. Learn how we collect, use, and protect
            your information.
          </p>
        </div>
      </div>

      {/* --- Main Content Section --- */}
      <div className="min-h-screen bg-white font-sans mt-[116px]">
        <div className="flex max-w-7xl mx-auto pb-20 pt-4 px-6 sm:px-8 -mt-20">
          {/* Sidebar */}
          <div className="w-[300px] p-4 bg-white rounded-xl mr-12 h-fit sticky top-6 hidden lg:block">
            {navItems.map((item) => (
  <div key={item.title} onClick={() => setActiveSection(item.title)}>
    <NavItem
      title={item.title}
      icon={item.icon}
      isActive={activeSection === item.title}
    />
  </div>
))}

          </div>

          {/* --- Main Policy Content --- */}
          <div className="w-full lg:w-3/4 p-2">
            <h2 className="text-2xl md:text-3xl font-light text-gray-800 mb-6">
              At API Galaxy, we value your privacy and are committed to
              protecting your personal information.
            </h2>
            <p className="text-gray-600 text-[16px] mb-10 leading-relaxed">
              This Privacy Policy explains how we collect, use, disclose, and
              safeguard your data when you access or use our website, APIs, and
              related services.
            </p>

            {/* Sectioned Policy Content */}
            <div className="space-y-10">
              {[
                {
                  title: "Information We Collect",
                  content: (
                    <>
                      <p className="mb-4">
                        We collect information to provide, secure, and improve
                        our API services. The data we collect falls into the
                        following categories:
                      </p>
                      <ul className="space-y-4">
                        <PolicyDetailItem
                          title="User Information"
                          content="Details you provide when registering or using our platform, such as your name, email address, phone number, and business details."
                        />
                        <PolicyDetailItem
                          title="Identity & Verification Data"
                          content="Information used for KYC/KYB verification including PAN, CIN, DIN, Passport, and other government-issued identifiers."
                        />
                        <PolicyDetailItem
                          title="Account & Transaction Data"
                          content="Information related to your login credentials, UPI ID, billing details, payment history, and API usage logs."
                        />
                        <PolicyDetailItem
                          title="Technical & Device Data"
                          content="Automatically collected details such as IP address, browser type, operating system, device identifiers, and activity timestamps to ensure platform security and performance."
                        />
                        <PolicyDetailItem
                          title="Communication & Support Data"
                          content="Information you share when contacting our support team, filling out forms, or participating in feedback sessions."
                        />
                      </ul>
                    </>
                  ),
                },
                {
                  title: "How We Use Your Information",
                  content: (
                    <ul className="space-y-3 list-disc list-inside text-gray-600">
                      <li>Provide, maintain, and improve our APIs and services.</li>
                      <li>Enable secure transactions and identity verification (KYC/KYB).</li>
                      <li>Comply with RBI, SEBI, NSDL, and AML regulations.</li>
                      <li>Detect, prevent, and mitigate fraud or unauthorized access.</li>
                      <li>Communicate updates, support responses, and service announcements.</li>
                      <li>Analyze performance and enhance the user experience.</li>
                    </ul>
                  ),
                },
                {
                  title: "Information Sharing and Disclosure",
                  content: (
                    <ul className="space-y-3 list-disc list-inside text-gray-600">
                      <li>With authorized partners and service providers who help operate our platform.</li>
                      <li>To comply with legal obligations, regulatory requests, or law enforcement.</li>
                      <li>During mergers, acquisitions, or business transitions, under strict confidentiality agreements.</li>
                    </ul>
                  ),
                },
                {
                  title: "Data Security",
                  content:
                    "We implement end-to-end encryption, IP whitelisting, and fraud detection systems to protect your data. Access is restricted to authorized personnel only.",
                },
                {
                  title: "Data Retention",
                  content:
                    "We retain personal data only as long as necessary for legal, operational, and compliance requirements. After this period, data is securely deleted or anonymized.",
                },
                {
                  title: "Your Rights",
                  content: (
                    <ul className="space-y-3 list-disc list-inside text-gray-600">
                      <li>Access and update your personal information.</li>
                      <li>Request deletion of your account and associated data.</li>
                      <li>Withdraw consent for specific data uses, subject to legal obligations.</li>
                    </ul>
                  ),
                },
                {
                  title: "Cookies and Tracking",
                  content:
                    "We use cookies and similar technologies to enhance user experience, track performance, and personalize services. You can manage or disable cookies through your browser settings.",
                },
                {
                  title: "Third-Party Links",
                  content:
                    "Our website may include links to external sites. We are not responsible for the privacy practices or content of third-party platforms.",
                },
                {
                  title: "Updates to This Policy",
                  content:
                    "We may update this Privacy Policy from time to time. The latest version will always be available on our website with the effective date clearly mentioned.",
                },
                {
                  title: "Contact Us",
                  content:
                    "For any questions, concerns, or privacy-related requests, please contact us at support@apigalaxy.in or visit www.apigalaxy.in.",
                },
              ].map((section, index) => (
                <div id={section.title.toLowerCase().replace(/\s/g, "-")} key={index}>
                  <h3 className="text-xl font-semibold text-[#2F56A6] mb-3">
                    {section.title}
                  </h3>
                  <div className="text-gray-600 text-[16px] leading-relaxed">
                    {section.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="h-40 w-full"></div>
      </div>
    </>
  );
};

export default PrivacyPolicy;
